/**
 * Analytics page.
 *
 * Layout (top → bottom):
 *   PageHeader → HeroPanel (visits / signups / activations / conversions / MRR)
 *              → Funnel sparkline strip (one mini-line per stage)
 *              → Source breakdown donut (substitutes os_platform for source —
 *                 marketing-source isn't in the licensing-server API)
 *              → Top-day bar list (top 10 highest-volume days in window)
 *              → Existing Trends grid (recharts area charts)
 *              → Existing Distributions grid (recharts donut + legend)
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  TrendingUp, RefreshCw, BarChart3, PieChart as PieIcon, ArrowDown,
} from 'lucide-react';
import {
  AdminApiClient,
  type TimeseriesMetric, type TimeseriesResponse,
  type DistributionMetric, type DistributionResponse,
} from '../api';
import { useSession } from '../session';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '../components/ui/Card';
import { ChartTooltip } from '../components/ui/ChartTooltip';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';
import { failureFrom, type Failure } from '../lib/api-state';

type RangePreset = '7d' | '30d' | '90d';
type Interval = 'day' | 'week';

const TIMESERIES_LABELS: Record<TimeseriesMetric, string> = {
  trials_created:      'Trials created',
  installer_downloads: 'Installer downloads',
  activations:         'Activations',
  paid_conversions:    'Paid conversions',
  mrr_cents:           'Revenue (USD)',
};

const FUNNEL_STAGES: Array<{ metric: TimeseriesMetric; label: string; tone: string }> = [
  { metric: 'trials_created',      label: 'Signups',     tone: '#fb923c' },
  { metric: 'installer_downloads', label: 'Downloads',   tone: '#fbbf24' },
  { metric: 'activations',         label: 'Activations', tone: '#22d3ee' },
  { metric: 'paid_conversions',    label: 'Paid',        tone: '#34d399' },
];

const DISTRIBUTIONS: Array<{ metric: DistributionMetric; title: string }> = [
  { metric: 'plan_tier',        title: 'Plan tier share' },
  { metric: 'license_status',   title: 'License status mix' },
  { metric: 'os_platform',      title: 'OS platform' },
  { metric: 'residency_region', title: 'Residency region' },
];

const PIE_COLORS = [
  '#818cf8', '#34d399', '#fbbf24', '#f87171',
  '#22d3ee', '#c084fc', '#fb923c', '#2dd4bf',
];

export function AnalyticsPage(): JSX.Element {
  const { state } = useSession();
  const [range, setRange] = useState<RangePreset>('30d');
  const [interval, setInterval] = useState<Interval>('day');
  const [series, setSeries] = useState<Partial<Record<TimeseriesMetric, TimeseriesResponse>>>({});
  const [dists, setDists] = useState<Partial<Record<DistributionMetric, DistributionResponse>>>({});
  // Per-source load state. A source that failed is "unavailable" (not zero):
  // its cards say so and the headline numbers that depend on it show "—".
  const [seriesErr, setSeriesErr] = useState<Partial<Record<TimeseriesMetric, Failure>>>({});
  const [distErr, setDistErr] = useState<Partial<Record<DistributionMetric, Failure>>>({});
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(0);

  const { since, until } = useMemo(() => {
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const u = new Date();
    const s = new Date(u.getTime() - days * 86_400_000);
    return { since: s.toISOString(), until: u.toISOString() };
  }, [range]);

  useEffect(() => {
    setRefreshing(true);
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    const metrics: TimeseriesMetric[] = [
      'trials_created', 'installer_downloads', 'activations',
      'paid_conversions', 'mrr_cents',
    ];
    Promise.all([
      Promise.allSettled(metrics.map((m) =>
        api.analyticsTimeseries({ metric: m, since, until, interval }))),
      Promise.allSettled(DISTRIBUTIONS.map(({ metric }) =>
        api.analyticsDistribution(metric))),
    ])
      .then(([ts, ds]) => {
        const nextSeries: Partial<Record<TimeseriesMetric, TimeseriesResponse>> = {};
        const nextSeriesErr: Partial<Record<TimeseriesMetric, Failure>> = {};
        ts.forEach((r, i) => {
          const m = metrics[i]!;
          if (r.status === 'fulfilled') nextSeries[m] = r.value;
          else nextSeriesErr[m] = failureFrom(r.reason, TIMESERIES_LABELS[m].toLowerCase());
        });
        const nextDists: Partial<Record<DistributionMetric, DistributionResponse>> = {};
        const nextDistErr: Partial<Record<DistributionMetric, Failure>> = {};
        ds.forEach((r, i) => {
          const m = DISTRIBUTIONS[i]!.metric;
          if (r.status === 'fulfilled') nextDists[m] = r.value;
          else nextDistErr[m] = failureFrom(r.reason, DISTRIBUTIONS[i]!.title.toLowerCase());
        });
        setSeries(nextSeries); setSeriesErr(nextSeriesErr);
        setDists(nextDists); setDistErr(nextDistErr);
        setLoaded(true);
      })
      .finally(() => setRefreshing(false));
  }, [state.token, since, until, interval, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  // null = this metric could not be loaded (unknown), distinct from a real 0.
  const totals = useMemo(() => {
    const sum = (m: TimeseriesMetric): number | null =>
      series[m] ? series[m]!.points.reduce((s, p) => s + p.value, 0) : null;
    const trials = sum('trials_created');
    const downloads = sum('installer_downloads');
    const activations = sum('activations');
    const conversions = sum('paid_conversions');
    const mrrCents = sum('mrr_cents');
    const pct = (num: number | null, den: number | null): number | null =>
      num === null || den === null || den <= 0 ? null : Math.round((num / den) * 100);
    return {
      trials, downloads, activations, conversions, mrrCents,
      conversionPct: pct(conversions, trials),
      activationPct: pct(activations, downloads),
    };
  }, [series]);
  const allSeriesFailed = loaded && Object.keys(series).length === 0 && Object.keys(seriesErr).length > 0;
  const allDistFailed = loaded && Object.keys(dists).length === 0 && Object.keys(distErr).length > 0;
  const firstFailure = Object.values(seriesErr)[0] ?? Object.values(distErr)[0] ?? null;
  const anyFailure = Object.keys(seriesErr).length + Object.keys(distErr).length > 0;
  const fmtN = (n: number | null): string | number => (n === null ? '—' : n);
  const pctHint = (p: number | null, of: string): string => (p === null ? 'not enough data' : `${p}% of ${of}`);

  return (
    <div className="page">
      <PageHeader
        title="Analytics"
        subtitle="Time-bucketed trends and current-state distributions across the licensing fleet."
        icon={BarChart3}
        accent="#818cf8"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw width={14} height={14}
                style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
              Refresh
            </Button>
            <RangeSelect value={range} onChange={setRange} />
            <select
              aria-label="Bucket size"
              value={interval}
              onChange={(e) => setInterval(e.target.value as Interval)}
              style={{
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
                padding: '6px 10px',
                borderRadius: 8,
                fontSize: '0.75rem',
              }}
            >
              <option value="day">Day buckets</option>
              <option value="week">Week buckets</option>
            </select>
          </>
        }
      />

      {!loaded && <FetchState loading skeleton="cards" />}
      {allSeriesFailed && allDistFailed && firstFailure && (
        <FetchState error={firstFailure} onRetry={onRefresh} />
      )}
      {loaded && !(allSeriesFailed && allDistFailed) && (
      <>
      {anyFailure && firstFailure && (
        <div style={{ marginBottom: 12 }}>
          <FetchState
            error={{ ...firstFailure, message: `Some figures could not be loaded and are shown as “—”. ${firstFailure.message}` }}
            onRetry={onRefresh}
          />
        </div>
      )}
      <HeroPanel
        icon={TrendingUp}
        accent="#818cf8"
        title="Pipeline volume"
        subtitle={`Sum of all events in the last ${range === '7d' ? '7 days' : range === '30d' ? '30 days' : '90 days'}.`}
        kpis={[
          <HeroKpi key="t" label="Signups" value={fmtN(totals.trials)} hint="trials created" />,
          <HeroKpi key="d" label="Downloads" value={fmtN(totals.downloads)}
                   band={totals.downloads !== null && totals.downloads > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="a" label="Activations" value={fmtN(totals.activations)}
                   band={totals.activationPct === null ? 'neutral'
                        : totals.activationPct >= 60 ? 'healthy'
                        : totals.activationPct >= 30 ? 'degraded'
                        : 'warning'}
                   hint={pctHint(totals.activationPct, 'downloads')} />,
          <HeroKpi key="c" label="Paid" value={fmtN(totals.conversions)}
                   band={totals.conversionPct === null ? 'neutral'
                        : totals.conversionPct >= 20 ? 'healthy'
                        : totals.conversionPct >= 5 ? 'degraded'
                        : 'warning'}
                   hint={pctHint(totals.conversionPct, 'trials')} />,
          <HeroKpi key="m" label="Invoiced revenue"
                   value={totals.mrrCents === null ? '—'
                     : `$${(totals.mrrCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                   hint="paid invoices in window" />,
        ]}
      />

      <div style={{ marginTop: 16 }}>
        <FunnelSparklineStrip series={series} />
      </div>

      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.6fr)',
        }}
      >
        <SourceBreakdown distribution={dists.os_platform} failed={!!distErr.os_platform} />
        <TopDays series={series.trials_created} failed={!!seriesErr.trials_created} />
      </div>

      <h3 className="section-title" style={{ marginTop: 28 }}>Trends</h3>
      <div className="analytics-trends">
        {(Object.keys(TIMESERIES_LABELS) as TimeseriesMetric[]).map((m) => (
          <TrendCard key={m} title={TIMESERIES_LABELS[m]} data={series[m]} error={seriesErr[m]}
                     transformValue={m === 'mrr_cents' ? (v) => v / 100 : undefined} />
        ))}
      </div>

      <h3 className="section-title" style={{ marginTop: 28 }}>Distributions</h3>
      <div className="analytics-dists">
        {DISTRIBUTIONS.map(({ metric, title }) => (
          <DistCard key={metric} title={title} data={dists[metric]} error={distErr[metric]} />
        ))}
      </div>
      </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Range selector
 * ────────────────────────────────────────────────────────────────── */

function RangeSelect({
  value, onChange,
}: { value: RangePreset; onChange: (v: RangePreset) => void }): JSX.Element {
  const opts: Array<{ key: RangePreset; label: string }> = [
    { key: '7d',  label: '7d' },
    { key: '30d', label: '30d' },
    { key: '90d', label: '90d' },
  ];
  return (
    <div
      role="group"
      aria-label="Date range"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 10,
        border: '1px solid var(--border)',
        background: 'var(--bg-overlay)',
      }}
    >
      {opts.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.key)}
            style={{
              padding: '4px 10px',
              border: 'none',
              borderRadius: 7,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Funnel sparkline strip — one mini-line per funnel stage
 * ────────────────────────────────────────────────────────────────── */

function FunnelSparklineStrip({
  series,
}: { series: Partial<Record<TimeseriesMetric, TimeseriesResponse>> }): JSX.Element {
  return (
    <section
      style={{
        padding: 14,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <ArrowDown width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Funnel pulse
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: `repeat(${FUNNEL_STAGES.length}, minmax(0, 1fr))`,
        }}
      >
        {FUNNEL_STAGES.map((s) => {
          const data = series[s.metric];
          const points = data?.points.map((p) => p.value) ?? [];
          const total = points.reduce((acc, v) => acc + v, 0);
          return (
            <div
              key={s.metric}
              style={{
                padding: 10,
                borderRadius: 10,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}
            >
              <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                {s.label}
              </span>
              <span
                style={{
                  fontSize: '1.125rem', fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color: s.tone,
                  lineHeight: 1.1,
                }}
              >
                {data ? total.toLocaleString() : '—'}
              </span>
              <Sparkline points={points} stroke={s.tone} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Sparkline({
  points, stroke, height = 28,
}: { points: number[]; stroke: string; height?: number }): JSX.Element {
  if (points.length === 0) return <div style={{ height }} />;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const w = 100;
  const h = 26;
  const step = w / (points.length - 1 || 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${(h - ((p - min) / range) * h).toFixed(2)}`)
    .join(' ');
  const area = `${path} L${w},${h} L0,${h} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={area} fill={stroke} fillOpacity={0.12} />
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Source breakdown donut (uses os_platform — closest proxy in API)
 * ────────────────────────────────────────────────────────────────── */

function SourceBreakdown({
  distribution, failed,
}: { distribution: DistributionResponse | undefined; failed?: boolean }): JSX.Element {
  const slices = (distribution?.slices ?? []).filter((s) => s.value > 0);
  const total = slices.reduce((s, o) => s + o.value, 0);

  return (
    <section
      style={{
        padding: 14,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <PieIcon width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Source breakdown · OS platform
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
          }}
        >
          {/* Marketing source isn't tracked in licensing data; this proxies via OS. */}
          proxied via fleet OS
        </span>
      </div>

      {slices.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }} role={failed ? 'alert' : undefined}>
          {failed ? 'Unavailable — could not load this breakdown.' : distribution ? 'No data yet.' : 'Loading…'}
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <DonutSvg slices={slices.map((s, i) => ({ value: s.value, color: PIE_COLORS[i % PIE_COLORS.length] ?? '#6366f1' }))}
                    total={total} />
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {slices.map((s, i) => {
              const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
              return (
                <li
                  key={s.label}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: '0.75rem',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span style={{ flex: 1, color: 'var(--text-primary)', textTransform: 'capitalize' }}>{s.label}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                    {s.value.toLocaleString()} ({pct}%)
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function DonutSvg({
  slices, total,
}: {
  slices: Array<{ value: number; color: string }>;
  total: number;
}): JSX.Element {
  const r = 32;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={92} height={92} viewBox="0 0 80 80" style={{ flex: 'none' }}>
      <circle cx={40} cy={40} r={r} fill="none" stroke="var(--bg-overlay)" strokeWidth={12} />
      {total > 0 && slices.map((s, i) => {
        if (s.value === 0) return null;
        const len = (s.value / total) * c;
        const dasharray = `${len} ${c - len}`;
        const dashoffset = -offset;
        offset += len;
        return (
          <circle
            key={i}
            cx={40} cy={40} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={12}
            strokeDasharray={dasharray}
            strokeDashoffset={dashoffset}
            transform="rotate(-90 40 40)"
          />
        );
      })}
      <text
        x={40} y={40}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="600"
        fill="var(--text-primary)"
      >
        {total}
      </text>
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Top days bar list (substitute for "top pages" — pages aren't a
 * concept in the licensing-server API, so we surface the highest-
 * volume signup days instead).
 * ────────────────────────────────────────────────────────────────── */

function TopDays({
  series, failed,
}: { series: TimeseriesResponse | undefined; failed?: boolean }): JSX.Element {
  const top = useMemo(() => {
    if (!series) return [];
    return series.points
      .map((p) => ({ bucket: p.bucket, value: p.value }))
      .filter((p) => p.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [series]);
  const max = Math.max(1, ...top.map((p) => p.value));

  return (
    <section
      style={{
        padding: 14,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <TrendingUp width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Top signup days
        </span>
      </div>

      {top.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }} role={failed ? 'alert' : undefined}>
          {failed ? 'Unavailable — could not load this data.' : series ? 'No activity in this window.' : 'Loading…'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {top.map((p) => {
            const pct = (p.value / max) * 100;
            return (
              <li
                key={p.bucket}
                style={{
                  position: 'relative',
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: 'var(--bg-overlay)',
                  overflow: 'hidden',
                }}
              >
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: `${pct}%`,
                    background: 'linear-gradient(90deg, rgba(129,140,248,0.18), rgba(129,140,248,0))',
                  }}
                />
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: '0.75rem',
                  }}
                >
                  <span style={{ color: 'var(--text-primary)' }}>
                    {p.bucket.slice(0, 10)}
                  </span>
                  <StatusChip band="healthy" size="sm" noDot>
                    {p.value} signups
                  </StatusChip>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * (Existing recharts cards preserved below)
 * ────────────────────────────────────────────────────────────────── */

function TrendCard({ title, data, error, transformValue }: {
  title: string;
  data: TimeseriesResponse | undefined;
  error?: Failure;
  transformValue?: (n: number) => number;
}): JSX.Element {
  const points = data?.points.map((p) => ({
    bucket: p.bucket.slice(0, 10),
    value: transformValue ? transformValue(p.value) : p.value,
  })) ?? [];
  const total = points.reduce((s, p) => s + p.value, 0);
  const gradientId = `grad-${title.replace(/\W/g, '')}`;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <CardTitle>{title}</CardTitle>
          <span style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--accent-text)' }}>
            {!data ? '—'
              : transformValue
              ? `$${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : total.toLocaleString()}
          </span>
        </div>
        <CardDescription>
          {data ? `${points.length} bucket${points.length === 1 ? '' : 's'}` : '—'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div style={{ height: 200 }}>
          {!data ? (
            <div className="chart-empty" role={error ? 'alert' : 'status'}>
              {error ? 'Unavailable — could not load this chart.' : 'Loading…'}
            </div>
          ) : points.length === 0 ? (
            <div className="chart-empty">No data in this window.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 6, right: 12, bottom: 0, left: -16 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                       tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                       tickLine={false} axisLine={false} allowDecimals={false} width={40} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2}
                      fill={`url(#${gradientId})`} fillOpacity={1} animationDuration={800}
                      name={title} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function DistCard({ title, data, error }: {
  title: string; data: DistributionResponse | undefined; error?: Failure;
}): JSX.Element {
  const slices = (data?.slices ?? []).filter((s) => s.value > 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {!data ? (
          <div className="chart-empty" style={{ height: 200 }} role={error ? 'alert' : 'status'}>
            {error ? 'Unavailable — could not load this chart.' : 'Loading…'}
          </div>
        ) : slices.length === 0 ? (
          <div className="chart-empty" style={{ height: 200 }}>No data.</div>
        ) : (
          <>
            <div style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices} cx="50%" cy="50%"
                    innerRadius={44} outerRadius={72} paddingAngle={3}
                    dataKey="value" nameKey="label" animationDuration={800}
                  >
                    {slices.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="transparent" />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-legend">
              {slices.map((s, i) => (
                <div key={s.label} className="chart-legend__item">
                  <span className="chart-legend__dot"
                        style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="chart-legend__label">{s.label}</span>
                  <span className="chart-legend__value">{s.value}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function HorizontalStackedBar(
  { data, title }: {
    data: { name: string; total: number; completed: number }[];
    title: string;
  },
): JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent>
        <div style={{ height: 210 }}>
          {data.length === 0 ? (
            <div className="chart-empty">No data.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" barCategoryGap="20%"
                        margin={{ top: 6, right: 12, bottom: 0, left: -6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                       tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name"
                       tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
                       tickLine={false} axisLine={false} width={80} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="completed" stackId="a" fill="#34d399"
                     name="Completed" animationDuration={800} />
                <Bar dataKey="total" stackId="a" fill="var(--border)"
                     radius={[0, 6, 6, 0]} name="Remaining" animationDuration={800} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
